import React, { useEffect, useState } from 'react';
import { Alert, Autocomplete, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import CalendarIcon from '../icons/CalendarIcon';
import { defaultReportOptions, REPORT_SCOPES, REPORT_SECTIONS, reportUrl } from './reportRequest';
import { useFeatureAccess } from '../billing/FeatureAccess';
import useReportSetup from './useReportSetup';

const referenceKey = document => JSON.stringify([document.source_table, document.id]);

export default function ReportExportDialog({ caseId, open, onClose }) {
  const allows = useFeatureAccess();
  const setup = useReportSetup(caseId, open, allows('documents'));
  const [scope, setScope] = useState('all');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [options, setOptions] = useState(defaultReportOptions);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [selection, setSelection] = useState([]);
  const hasAssociationPeriods = setup.metadata?.available_scopes.includes('before_association')
    && setup.metadata?.available_scopes.includes('after_association');
  const selectedScope = hasAssociationPeriods ? scope : 'all';
  const documents = selectedScope === 'before_association' ? setup.metadata?.before_documents || [] : setup.documents;
  const selectedDocuments = selection.filter(selected => documents.some(document => referenceKey(document) === referenceKey(selected)));
  const required = !hasAssociationPeriods;
  useEffect(() => {
    if (!setup.metadata || hasAssociationPeriods) return;
    setFromDate(previous => previous || (setup.metadata.default_from_date ? dayjs(setup.metadata.default_from_date) : null));
    setToDate(previous => previous || dayjs());
  }, [setup.metadata, hasAssociationPeriods]);
  const changePeriod = value => { setScope(value); setSelection([]); setError(''); };
  const generate = () => {
    setTouched(true);
    try {
      if (setup.loading || setup.error || !setup.metadata) return;
      if ((required && (!fromDate || !toDate)) || (fromDate && !fromDate.isValid()) || (toDate && !toDate.isValid())) {
        throw new Error('Choose a valid From and To date.');
      }
      if (fromDate?.isAfter(dayjs(), 'day') || toDate?.isAfter(dayjs(), 'day')) throw new Error('Dates cannot be in the future.');
      const permittedOptions = Object.fromEntries(Object.entries(options).map(([key, value]) => [key, value && allows(key)]));
      const url = reportUrl(caseId, { scope: selectedScope, from_date: fromDate?.format('YYYY-MM-DD'), to_date: toDate?.format('YYYY-MM-DD'),
        options: permittedOptions, document_ids: permittedOptions.documents ? selectedDocuments.map(({ source_table, id }) => ({ source_table, id })) : [] });
      window.open(url, '_blank', 'noopener,noreferrer');
      onClose();
    } catch (err) { setError(err.message); }
  };
  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>Case Summary Report</DialogTitle>
    <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
      {hasAssociationPeriods && <TextField select label="Reporting period" value={selectedScope} onChange={event => changePeriod(event.target.value)}>
        {setup.metadata.available_scopes.map(value => <MenuItem key={value} value={value}>{REPORT_SCOPES[value]}</MenuItem>)}
      </TextField>}
      <Typography variant="body1">{required ? 'Range' : 'Range (optional)'}</Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack direction="row" spacing={2}>
          <DatePicker label="From" value={fromDate} disableFuture slots={{ openPickerIcon: CalendarIcon }}
            onChange={value => { setFromDate(value); setError(''); }}
            slotProps={{ textField: { fullWidth: true, required, error: touched && ((required && !fromDate) || (!!fromDate && !fromDate.isValid())) } }} />
          <DatePicker label="To" value={toDate} disableFuture minDate={fromDate?.isValid() ? fromDate : undefined} slots={{ openPickerIcon: CalendarIcon }}
            onChange={value => { setToDate(value); setError(''); }}
            slotProps={{ textField: { fullWidth: true, required, error: touched && ((required && !toDate) || (!!toDate && !toDate.isValid())) } }} />
        </Stack>
      </LocalizationProvider>
      <Stack>{Object.entries(REPORT_SECTIONS).map(([key, label]) => <FormControlLabel key={key}
        control={<Checkbox checked={options[key] && allows(key)} disabled={!allows(key)} onChange={event => setOptions(previous => ({ ...previous, [key]: event.target.checked }))} />}
        label={allows(key) ? label : `${label} (not included in this plan)`} />)}</Stack>
      {allows('documents') && <><Divider sx={{ my: 1.5 }} />
        <Typography variant="body1" fontWeight="500">Choose Documents to include in the report</Typography>
        <Autocomplete multiple autoHighlight filterSelectedOptions options={documents} value={selectedDocuments}
          loading={selectedScope !== 'before_association' && setup.documentsLoading}
          isOptionEqualToValue={(option, value) => referenceKey(option) === referenceKey(value)}
          getOptionLabel={document => document.name || `Document ${document.id}`}
          onChange={(_, values) => setSelection(values)}
          renderInput={params => <TextField {...params} inputProps={{ ...params.inputProps, 'aria-label': 'Choose Documents to include in the report' }} />} />
        {setup.documentsError && selectedScope !== 'before_association' && <Alert severity="error" action={<Button onClick={setup.retry}>Retry</Button>}>{setup.documentsError}</Alert>}
      </>}
      {setup.loading && <Typography role="status">Loading export options…</Typography>}
      {setup.error && <Alert severity="error" action={<Button onClick={setup.retry}>Retry</Button>}>{setup.error}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack></DialogContent>
    <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" disabled={setup.loading || !!setup.error || !setup.metadata}
      onClick={generate}>Generate report</Button></DialogActions>
  </Dialog>;
}
